import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Plot } from '../../../models/plot';
import { Report } from '../../../models/report.model';
import { GeoService } from '../../../services/geo.service';

@Component({
  selector: 'app-cadastre-edit-mode',
  templateUrl: './cadastre-edit-mode.component.html',
  styleUrls: ['./cadastre-edit-mode.component.scss'],
})
export class CadastreEditModeComponent implements OnChanges {
  @Input() cadastre!: Report.Cadastre;
  @Input() cityLabel!: string;
  @Input() cityCode!: string;
  @Input() resetKey = 0;

  @Output() cadastreChanged = new EventEmitter<Report.Cadastre>();

  cadastreForm = new FormGroup({
    plotPrefixe: new FormControl('', [Validators.maxLength(3)]),
    plotSection: new FormControl('', [Validators.required]),
    plotNumber: new FormControl('', [Validators.required]),
    comment: new FormControl(''),
    cityLabel: new FormControl(''),
  });

  plot!: Plot;
  plotRef!: string;
  isPlotFound = false;
  hasChecked = false;
  isLoading = false;

  private newCadastre!: Report.Cadastre;
  private previousResetKey = 0;

  private readonly DEFAULT_VALUES = {
    plotPrefixe: '000',
    plotSection: '00',
    plotNumber: '0000',
  } as const;

  private readonly FIELD_CONFIGS = {
    plotPrefixe: { length: 3, defaultValue: this.DEFAULT_VALUES.plotPrefixe },
    plotSection: { length: 2, defaultValue: this.DEFAULT_VALUES.plotSection },
    plotNumber: { length: 4, defaultValue: this.DEFAULT_VALUES.plotNumber },
  } as const;

  get isReadyforCheck(): boolean {
    return !!(
      this.cadastreForm.controls.plotSection.value &&
      this.cadastreForm.controls.plotNumber.value
    );
  }

  constructor(private readonly geoService: GeoService) {
    this.hasChecked = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.handleResetKeyChange(changes);
    this.handleCadastreChange(changes);
  }

  private handleResetKeyChange(changes: SimpleChanges): void {
    if (changes['resetKey'] && this.resetKey !== this.previousResetKey) {
      this.resetCheckState();
      this.previousResetKey = this.resetKey;
    }
  }

  private handleCadastreChange(changes: SimpleChanges): void {
    const currentCadastre = changes['cadastre']?.currentValue;
    if (currentCadastre) {
      this.newCadastre = currentCadastre;
      this.setFormValues();
    }
  }

  plotPrefixeChanged(event: Event): void {
    this.updateCadastreField('plotPrefixe', event);
  }

  plotSectionChanged(event: Event): void {
    this.updateCadastreField('plotSection', event);
  }

  plotNumberChanged(event: Event): void {
    this.updateCadastreField('plotNumber', event);
  }

  commentChanged(event: Event): void {
    this.updateCadastreField('comment', event);
  }

  private updateCadastreField(
    field: keyof Report.Cadastre,
    event: Event,
  ): void {
    const value = (event.target as HTMLInputElement).value;
    this.newCadastre = {
      ...this.newCadastre,
      [field]: value,
    };
    this.cadastreChanged.emit(this.newCadastre);
  }

  async onCheckCadastre(): Promise<void> {
    this.resetCheckState();
    this.setLoadingState(true);

    try {
      const formattedPlotRef = this.formatPlotReference();
      this.plotRef = formattedPlotRef;

      this.plot = await this.geoService.getCadastre(
        this.removeSpacesFromPlotRef(formattedPlotRef),
      );

      this.isPlotFound = true;
    } catch {
      this.isPlotFound = false;
    } finally {
      this.finalizeCheck();
    }
  }

  private resetCheckState(): void {
    this.hasChecked = false;
    this.isPlotFound = false;
    this.plot = undefined as any;
  }

  private setLoadingState(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  private formatPlotReference(): string {
    const plotPrefixe = this.formatFieldValue('plotPrefixe');
    const plotSection = this.formatSection(this.newCadastre.plotSection);
    const plotNumber = this.formatFieldValue('plotNumber');

    return `${this.cityCode} ${plotPrefixe} ${plotSection} ${plotNumber}`;
  }

  private formatFieldValue(field: keyof typeof this.FIELD_CONFIGS): string {
    const config = this.FIELD_CONFIGS[field];
    return this.padStartWithZeros(this.newCadastre[field], config.length);
  }

  private padStartWithZeros(value: string, length: number): string {
    return value.padStart(length, '0');
  }

  private formatSection(section: string): string {
    return section.toUpperCase().padStart(2, '0');
  }

  private removeSpacesFromPlotRef(plotRef: string): string {
    return plotRef.replace(/\s/g, '');
  }

  private finalizeCheck(): void {
    this.hasChecked = true;
    this.isLoading = false;
    this.updateFormWithFormattedValues();
  }

  private updateFormWithFormattedValues(): void {
    const formattedValues = {
      plotPrefixe: this.formatFieldValue('plotPrefixe'),
      plotSection: this.formatSection(this.newCadastre.plotSection),
      plotNumber: this.formatFieldValue('plotNumber'),
    };

    this.newCadastre = {
      ...this.newCadastre,
      ...formattedValues,
    };

    this.cadastreChanged.emit(this.newCadastre);

    Object.entries(formattedValues).forEach(([field, value]) => {
      this.cadastreForm.controls[
        field as keyof typeof formattedValues
      ].setValue(value);
    });
  }

  private setFormValues(): void {
    this.cadastreForm.controls.cityLabel.setValue(this.cityLabel);
    this.setPlotFieldValues();
    this.cadastreForm.controls.comment.setValue(this.newCadastre.comment);
    this.checkPlotReferenceChange();
  }

  private setPlotFieldValues(): void {
    Object.entries(this.FIELD_CONFIGS).forEach(([field, config]) => {
      const value = this.newCadastre[field as keyof typeof this.FIELD_CONFIGS];
      if (value !== config.defaultValue) {
        this.cadastreForm.controls[
          field as keyof typeof this.FIELD_CONFIGS
        ].setValue(value);
      }
    });
  }

  private checkPlotReferenceChange(): void {
    const formattedPlotRef = this.formatPlotReference();
    if (formattedPlotRef !== this.plotRef) {
      this.hasChecked = false;
    }
  }
}
